import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Spin, Alert, Statistic } from 'antd';
import { ClockCircleOutlined, UserOutlined, BarChartOutlined } from '@ant-design/icons';
import { Column } from '@ant-design/charts';
import driverApi from '../../api/driver';
import StatisticNumber from '../../components/StatisticNumber';

const { Title, Text } = Typography;

// Helper function to convert minutes to hours and minutes format
const formatMinutesToHours = (minutes) => {
  if (!minutes || minutes === 0) return '0h 0p';
  
  // Round the minutes to nearest integer
  const roundedMinutes = Math.round(minutes);
  
  const hours = Math.floor(roundedMinutes / 60);
  const remainingMinutes = roundedMinutes % 60;
  
  if (hours === 0) {
    return `${remainingMinutes}p`;
  } else if (remainingMinutes === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${remainingMinutes}p`;
  }
};

const StatisticTime = ({ driver_id }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState(null);

  useEffect(() => {
    if (driver_id) {
      fetchStatistics();
    }
  }, [driver_id]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use driver_id from props
      if (!driver_id) {
        setError('Driver ID is required');
        return;
      }
      
      // Fetch driver statistics for recent 3 months
      const response = await driverApi.getStatistics(driver_id, 3);
      
      // For demo purposes, using the provided data structure
      // In real implementation, you would use the actual API response
      
      
      // Use actual API response if available, otherwise use demo data
      setStatistics(response);
    } catch (err) {
      setError('Failed to fetch statistics data. Please try again.');
      console.error('Error fetching statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  const prepareChartData = () => {
    if (!statistics?.statistics) return [];
    
    return statistics.statistics.map(stat => ({
      month: stat.month,
      standby_hours: stat.total_standby_hours,
      working_hours: stat.total_working_hours
    }));
  };

  const chartConfig = {
    data: prepareChartData(),
    xField: 'month',
    yField: 'value',
    seriesField: 'type',
    isGroup: true,
    columnStyle: {
      radius: [4, 4, 0, 0],
    },
    label: {
      position: 'middle',
      layout: [
        { type: 'interval-adjust-position' },
        { type: 'interval-hide-overlap' },
        { type: 'adjust-color' },
      ],
    },
    meta: {
      value: {
        alias: 'Hours',
      },
    },
    color: ['#1890ff', '#52c41a'],
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>
          <Text>Loading statistics...</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        action={
          <Text strong onClick={fetchStatistics} style={{ cursor: 'pointer' }}>
            Retry
          </Text>
        }
      />
    );
  }

  if (!statistics) {
    return (
      <Alert
        message="No Data"
        description="No statistics data available."
        type="info"
        showIcon
      />
    );
  }

 

  return (
    <div style={{ padding: '12px' }}>
      
      
      {/* Driver Info */}
      {/* <Card style={{ marginBottom: '24px' }}>
        <Row gutter={16} align="middle">
          <Col>
            <UserOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
          </Col>
          <Col flex="auto">
            <Title level={4} style={{ margin: 0 }}>
              {statistics.driver_name}
            </Title>
            <Text type="secondary">Driver ID: {statistics.driver_id}</Text>
          </Col>
        </Row>
      </Card> */}

      {/* Summary Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={12}>
          <Card>
            <StatisticNumber
              title="Thời gian online"
              value={formatMinutesToHours(statistics.total_standby_minutes)}
              unit=""
              showPrefix={false}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={12}>
          <Card>
            <StatisticNumber
              title="Thời gian làm việc"
              value={formatMinutesToHours(statistics.total_working_minutes)}
              unit=""
              showPrefix={false}
            />
          </Card>
        </Col>
        
      </Row>

      {/* Monthly Chart */}
      

      {/* Detailed Monthly Data */}
      <Card title="Chi tiết theo tháng">
        <div style={{ width: '100%' }}>
          {statistics.statistics.map((stat, index) => (
            <div 
              key={index} 
              style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '12px 0',
                borderBottom: index < statistics.statistics.length - 1 ? '1px solid #f0f0f0' : 'none'
              }}
            >
              <div style={{ flex: 1 }}>
                <Text strong>{stat.month}</Text>
              </div>
              <div style={{ display: 'flex', gap: '24px' }}>
                <div style={{ textAlign: 'center' }}>
                  <Text type="secondary">Thời gian chờ</Text>
                  <div style={{ fontSize: '16px', color: '#1890ff', fontWeight: 'bold' }}>
                    {formatMinutesToHours(stat.total_standby_minutes)}
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <Text type="secondary">Thời gian làm việc</Text>
                  <div style={{ fontSize: '16px', color: '#52c41a', fontWeight: 'bold' }}>
                    {formatMinutesToHours(stat.total_working_minutes)}
                  </div>
                </div>
                
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default StatisticTime;
